// Parse a pasted-in / uploaded quiz file into MCQ objects.
// Auto-detects JSON vs free-form text based on content sniffing.
//
// Text format accepted (loose):
//   "Which outcome is riskiest? A Food poisoning B Reputation damage
//    C Allergic reactions D Nothing (C is correct)"
//
// Handles:
//   - 4-6 options (A B C D E F)
//   - Multiple questions in one file (blank-line, numbered, or one per line)
//   - Numbered prefixes ("1.", "Q1)", etc.)
//   - Option markers "A", "A.", "A)", "A:" — always AFTER the "?" so single-letter
//     words like "a" or "I" inside the stem don't get mis-detected.
//   - Correct-answer markers "(C is correct)", "Answer: C", "Correct: C"
//
// JSON format:
//   [{ "question": "...", "options": ["...","..."], "correct_index": 0 }, ...]

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID)
  ? crypto.randomUUID()
  : Math.random().toString(36).slice(2);

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];

function parseJsonQuiz(text) {
  const data = JSON.parse(text);
  const arr = Array.isArray(data) ? data : (data.questions || data.quiz_questions || []);
  const questions = [];
  const errors = [];
  for (const raw of arr) {
    if (!raw || typeof raw !== "object") continue;
    const q = String(raw.question || raw.stem || "").trim();
    const opts = Array.isArray(raw.options) ? raw.options.map((o) => String(o).trim()) : [];
    if (!q || opts.length < 2) { errors.push(`Skipped: "${q.slice(0, 60)}"`); continue; }
    let ci = Number(raw.correct_index ?? raw.correct ?? 0);
    if (typeof raw.correct === "string" && /^[a-z]$/i.test(raw.correct.trim())) {
      ci = raw.correct.trim().toUpperCase().charCodeAt(0) - 65;
    }
    if (!Number.isFinite(ci) || ci < 0 || ci >= opts.length) ci = 0;
    while (opts.length < 4) opts.push("");
    questions.push({ id: uid(), question: q, options: opts.slice(0, 6), correct_index: ci });
  }
  return { questions, errors };
}

function parseOneBlock(block) {
  if (!block) return null;
  // Strip leading numbering (1., Q1), Question 1:, or bare "Question 1" on its own line).
  let text = block
    .replace(/^\s*(?:Question|Q)\s+\d+\s*\n/i, "")
    .replace(/^\s*(?:Question\s*|Q\s*)?\d+\s*[.):-]\s*/i, "")
    .trim();

  // Extract correct-answer marker BEFORE searching for options
  let correctLetter = null;
  const correctPatterns = [
    /\(\s*([A-F])\s+is\s+(?:the\s+)?correct(?:\s+answer)?\s*\)/i,
    /\bCorrect\s+answer\s*[:=]\s*([A-F])\b/i,
    /\bCorrect\s*[:=]\s*([A-F])\b/i,
    /\bAnswer\s*[:=]\s*([A-F])\b/i,
    /\*\s*([A-F])\s*\*/,
  ];
  for (const re of correctPatterns) {
    const m = text.match(re);
    if (m) {
      correctLetter = m[1].toUpperCase();
      text = (text.slice(0, m.index) + text.slice(m.index + m[0].length)).trim();
      break;
    }
  }

  // -------- Locate the stem/options boundary --------
  // Prefer the first "?" as the end of the stem; fall back to first newline; else no split hint.
  // The option A marker MUST come AFTER this boundary, which prevents "in a setting"
  // from being mis-detected as option A.
  let boundary = -1;
  const qIdx = text.search(/[?？]/);
  if (qIdx >= 0) boundary = qIdx + 1;
  else {
    const nlIdx = text.search(/\n/);
    if (nlIdx >= 0) boundary = nlIdx + 1;
  }

  // -------- Scan for option markers in ascending order --------
  // First pass: strict — require boundary and only take markers after it.
  // Second pass: fallback — no boundary, accept from start (for question-less prompts).
  const tryFrom = (startAt) => {
    const positions = [];
    let cursor = startAt;
    for (const letter of OPTION_LETTERS) {
      // Marker = standalone letter preceded by start-of-string/whitespace/newline,
      // followed by punctuation (.):]-) OR whitespace-then-uppercase (typical option text).
      // Requiring the following character to be either a delimiter or an uppercase letter
      // rules out lowercase-word cases like "a nursery" or "I am".
      const re = new RegExp(
        `(?:^|[\\s\\n\\r])(${letter})(?:[.):\\]\\-]|\\s+(?=[A-Z0-9"'\`]))`,
        "gi"
      );
      re.lastIndex = cursor;
      const m = re.exec(text);
      if (!m) break;
      const letterOffset = m[0].indexOf(m[1]);
      positions.push({
        letter,
        startInBlock: m.index + letterOffset,
        textStart: m.index + m[0].length,
      });
      cursor = m.index + m[0].length;
    }
    return positions;
  };

  let positions = boundary >= 0 ? tryFrom(boundary) : [];
  if (positions.length < 2) {
    positions = tryFrom(0);
  }
  if (positions.length < 2) return null;

  const stem = text.slice(0, positions[0].startInBlock)
    .trim()
    .replace(/[:\-–—]\s*$/, "")
    .trim();
  if (!stem) return null;

  const options = [];
  for (let i = 0; i < positions.length; i++) {
    const end = i + 1 < positions.length ? positions[i + 1].startInBlock : text.length;
    const raw = text.slice(positions[i].textStart, end).trim().replace(/[;,.]$/, "").trim();
    options.push(raw);
  }
  while (options.length < 4) options.push("");

  let correctIndex = 0;
  if (correctLetter) {
    const idx = positions.findIndex((p) => p.letter === correctLetter);
    if (idx >= 0) correctIndex = idx;
  }

  return { id: uid(), question: stem, options: options.slice(0, 6), correct_index: correctIndex };
}

function parseTextQuiz(text) {
  text = text.replace(/\r\n/g, "\n").trim();

  // Try several splitting strategies and take whichever yields the most parseable questions.
  const strategies = [
    // 1) Line by line
    text.split(/\n+/).map((s) => s.trim()).filter(Boolean),
    // 2) Blank-line separated blocks
    text.split(/\n\s*\n+/).map((s) => s.trim()).filter(Boolean),
    // 3) Split at numbered prefixes at line start ("1.", "Q1)", "Question 1:")
    text.split(/\n(?=\s*(?:Question\s*|Q\s*)?\d+\s*[.):]\s+)/i)
      .map((s) => s.trim()).filter(Boolean),
    // 4) Split at bare "Question N" / "Q N" headers at line start (no punctuation).
    //    This handles the copy-paste format:
    //      Question 1
    //      <stem>
    //      A. ...
    //      Correct Answer: B
    //      Question 2
    //      ...
    text.split(/\n(?=\s*(?:Question|Q)\s+\d+\s*$)/im)
      .map((s) => s.trim()).filter(Boolean),
    // 5) Whole document as a single block
    [text],
  ];

  let best = { questions: [], errors: [] };
  for (const blocks of strategies) {
    const parsed = [];
    const failed = [];
    for (const b of blocks) {
      const q = parseOneBlock(b);
      if (q) parsed.push(q);
      else if (b.length > 6) failed.push(b);
    }
    if (parsed.length > best.questions.length) {
      best = {
        questions: parsed,
        errors: failed.map(
          (f) => `Could not parse: "${f.slice(0, 60).replace(/\s+/g, " ")}${f.length > 60 ? "…" : ""}"`
        ),
      };
    }
  }

  // Always surface at least one failure message when nothing was parseable
  // so the caller's toast is actionable.
  if (best.questions.length === 0 && best.errors.length === 0 && text.length > 0) {
    best.errors.push(`Could not parse: "${text.slice(0, 60).replace(/\s+/g, " ")}${text.length > 60 ? "…" : ""}"`);
  }
  return best;
}

export function parseQuizFile(filename, text) {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  const trimmed = text.trim();
  if (ext === "json" || trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try { return parseJsonQuiz(trimmed); }
    catch (e) { return { questions: [], errors: [`Invalid JSON: ${e.message}`] }; }
  }
  return parseTextQuiz(trimmed);
}
